import React, { useState, useCallback, useMemo, memo, useEffect } from 'react';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { InteractiveQuestion } from './InteractiveQuestion';
import { QUESTION_REASON } from '@config/constants';

/**
 * QuestionGroup Component
 * Displays a group of questions (3-20) based on adaptive sizing
 * Handles:
 * - Multiple question types
 * - Group progress tracking
 * - Context display for spaced review and review rounds
 * - Answer validation and feedback
 * - Question prefetching for faster navigation
 * - Non-blocking answer submission
 */
export const QuestionGroup = memo(function QuestionGroupContent({
  questions = [],
  groupSize = 5,
  reinforcementCount = 0,
  currentSectionId = null,
  message = 'Answer the following questions',
  isSpacedReviewContext = false,
  isReviewRound = false,
  onSubmit = null,
  loading = false,
  userId = null,
  courseId = null,
  course = null,
}) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset index when questions array changes (new group arrives)
  useEffect(() => {
    setCurrentQuestionIndex(0);
    setAnswers({});
  }, [questions]);

  // All hooks must be declared before any early return (Rules of Hooks)
  const currentQuestion = useMemo(
    () => (questions && questions.length > 0 ? questions[currentQuestionIndex] : null),
    [questions, currentQuestionIndex]
  );
  const totalQuestions = questions ? questions.length : 0;
  const progressPercent = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;
  const inferredReinforcementCount = useMemo(() => {
    if (reinforcementCount > 0) {
      return reinforcementCount;
    }
    if (!currentSectionId) {
      return 0;
    }
    return questions.filter((q) => q?.sectionId && q.sectionId !== currentSectionId).length;
  }, [reinforcementCount, currentSectionId, questions]);

  // Memoized callback to prevent re-creation on parent renders.
  // InteractiveQuestion calls onSubmit({ questionId, userAnswer, isCorrect, ... })
  const handleAnswer = useCallback(async (answerPayload) => {
    try {
      setSubmitting(true);
      setSubmitError(null);

      const { questionId, userAnswer, isCorrect, hintsUsed, attempts, fiftyFiftyUsed, reviewContentUsed, tags } = answerPayload || {};

      // Store answer immediately (don't wait for parent)
      setAnswers(prev => ({
        ...prev,
        [questionId]: { value: userAnswer, isCorrect },
      }));

      const isLastQuestion = currentQuestionIndex + 1 >= totalQuestions;
      
      // Move to next question immediately for instant feedback (unless last question)
      if (!isLastQuestion) {
        setCurrentQuestionIndex(prev => prev + 1);
      }

      console.log(`[QuestionGroup] Submitting answer for Q${currentQuestionIndex + 1}/${totalQuestions}`, {
        isLastQuestion,
        isCorrect,
      });

      // Call parent submit handler (non-blocking for non-last questions)
      if (onSubmit) {
        const submitData = {
          questionId,
          value: userAnswer,
          isCorrect,
          isLastQuestion,
          tags: tags || [],
          hintsUsed,
          attempts,
          fiftyFiftyUsed,
          reviewContentUsed,
        };
        // For last question, we need to wait to get the next group
        if (isLastQuestion) {
          console.log('[QuestionGroup] Last question - waiting for next group...');
          await onSubmit(submitData);
        } else {
          // For other questions, don't wait
          onSubmit(submitData).catch(err => {
            console.error('[QuestionGroup] Error submitting answer:', err);
            setSubmitError(err.message || 'Failed to submit answer');
          });
        }
      }
    } catch (err) {
      console.error('[QuestionGroup] Error submitting answer:', err);
      setSubmitError(err.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  }, [currentQuestionIndex, totalQuestions, onSubmit]);

  if (!questions || questions.length === 0) {
    return (
      <Card className="text-center">
        <Card.Body className="py-5">
          <Alert variant="info">No questions available for this section.</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="question-group">
      {/* Review Round Banner */}
      {isReviewRound && (
        <Alert variant="warning" className="mb-3">
          <strong>📝 Review:</strong> You did not reach the required score on your first attempt. Review the section content and try again.
        </Alert>
      )}

      {/* Context Badge for Spaced Review */}
      {isSpacedReviewContext && (
        <Alert variant="info" className="mb-3">
          <strong>🔄 Spaced Review:</strong> Quick review of earlier material to prevent forgetting
        </Alert>
      )}

      {!isSpacedReviewContext && inferredReinforcementCount > 0 && (
        <Alert variant="secondary" className="mb-3">
          <strong>📚 Review Mix:</strong> This round includes {inferredReinforcementCount} reinforcement question{inferredReinforcementCount !== 1 ? 's' : ''} from earlier sections.
        </Alert>
      )}

      {/* Message */}
      {message && (
        <p className="text-muted mb-4">{message}</p>
      )}

      {/* Group Progress */}
      <Card className="mb-4 bg-light border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </span>
            <span className="badge bg-primary">{groupSize} question{'s' === (groupSize !== 1 ? 's' : '')}</span>
          </div>
          <div className="progress" style={{ height: '8px' }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${progressPercent}%` }}
              aria-valuenow={currentQuestionIndex + 1}
              aria-valuemin="1"
              aria-valuemax={totalQuestions}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {submitError && (
        <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
          {submitError}
        </Alert>
      )}

      {/* Current Question */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          {loading && !currentQuestion ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" className="mb-3" />
              <p className="text-muted">Loading question...</p>
            </div>
          ) : currentQuestion ? (
            <InteractiveQuestion
              question={currentQuestion}
              userId={userId}
              courseId={courseId}
              currentSectionId={currentSectionId}
              questionSectionId={currentQuestion?.sectionId || currentSectionId}
              course={course}
              onSubmit={handleAnswer}
              disabled={submitting}
              reason={
                currentQuestion?.reason ||
                (currentQuestion?.isReinforcement ||
                (currentQuestion?.sectionId && currentSectionId && currentQuestion.sectionId !== currentSectionId)
                  ? QUESTION_REASON.TIME
                  : QUESTION_REASON.CURRENT)
              }
              timesShown={currentQuestion?.timesShown || 0}
            />
          ) : (
            <Alert variant="warning">No question available</Alert>
          )}
        </Card.Body>
      </Card>

      {/* Progress Indicator */}
      <div className="text-center mb-3">
        <small className="text-muted">
          {answers[currentQuestion?.id] && '✓ '}
          {currentQuestionIndex + 1} / {totalQuestions}
        </small>
      </div>
    </div>
  );
});

QuestionGroup.displayName = 'QuestionGroup';
